// apps/web/src/components/admin-nav-permissions.ts
//
// F-065 — an Operator saw the entire Admin surface.
//
// Thirteen `/admin/*` links rendered unconditionally; only five were gated. The
// five that WERE gated (queue-blocks, queue-instructions, teams, announcements,
// staff-monitor) are the pattern this module generalises: read the bare-action
// permission list off the session and render the entry only when the session
// carries a capability its surface actually needs.
//
// Two rules hold this together:
//
//   1. Every entry declares its capability here, in one table, so a new admin
//      surface cannot be added ungated by accident — AppShell has no ungated
//      branch left to copy.
//   2. The whole Admin section (and the separator above it) renders only when
//      at least one entry is visible. That is DERIVED from this table rather
//      than a second hand-maintained list, so the section can never outlive its
//      contents.
//
// Capability vocabulary is `TENANT_CAPABILITIES` in
// features/admin/components/permissions-vocab.ts — the ids ARE the stored
// actions ARE the session keys, no translation layer. Where a surface has no
// obvious capability the closest `manage-*` is used rather than leaving it
// ungated; those choices are called out per entry below.
//
// Hiding a link is NOT access control — the server gates are the boundary. This
// module exists so an operator is not shown doors that will 403.

/** Any-of semantics: the entry renders when the session holds ANY listed capability. */
export type AdminNavKey =
  | "jobTypes"
  | "failedDocuments"
  | "opsConsole"
  | "ocrHealth"
  | "customCounters"
  | "queueBlocks"
  | "queueInstructions"
  | "teams"
  | "announcements"
  | "directedNotes"
  | "staffMonitor"
  | "retention"
  | "history"
  | "classify"
  | "pendingRelease"
  | "lookupTables"
  | "namedChains"
  | "transformPlugins"
  | "inputChannels"
  | "exportFormats"
  | "exportHistory"
  | "outputs"
  | "connections"
  | "agents"
  | "llm"
  | "llmUsage"
  | "roles"
  | "tenantSettings"
  | "reports"
  | "analytics";

export const ADMIN_NAV_CAPABILITIES: Record<AdminNavKey, readonly string[]> = {
  // --- Forms ---
  jobTypes: ["manage-job-types"],

  // --- Operations ---
  failedDocuments: ["monitor-intervene"],
  // Matches the server gate on /api/ops/* (supervisor.ops_console).
  opsConsole: ["supervisor.ops_console"],
  // Matches the server gate in routes/admin/ocr-health.ts.
  ocrHealth: ["admin-ocr-dashboard"],
  // Matches the server gate in routes/custom-counters/index.ts.
  customCounters: ["manage:custom-counters"],
  queueBlocks: ["supervisor.queue_block"],
  queueInstructions: ["supervisor.queue_instruct"],
  teams: ["team.manage"],
  announcements: ["supervisor.announce"],
  // Kept in step with canSendDirectNote (supervisor.direct_note OR admin).
  directedNotes: ["supervisor.direct_note", "admin"],
  staffMonitor: ["supervisor.ops_console"],
  retention: ["monitor-intervene"],
  history: ["monitor-intervene"],
  // Ambiguous operator/admin split: the classify QUEUE is a supervisory triage
  // surface, but a custom "classifier" role reclassifies documents without
  // holding monitor-intervene — so either capability opens it.
  classify: ["monitor-intervene", "reclassify-doc"],
  // Same shape: releasing held bundles is supervisory, but a batch manager
  // legitimately does it.
  pendingRelease: ["monitor-intervene", "batch.manage"],

  // --- Data ---
  // No dedicated capability exists for reference data; these feed job
  // configuration, so the closest manage-* applies.
  lookupTables: ["manage-job-types"],
  // Matches the server gates in routes/named-chains + routes/transform-plugins.
  namedChains: ["manage-rules"],
  transformPlugins: ["manage-rules"],

  // --- Integrations ---
  inputChannels: ["manage-input-channels"],
  exportFormats: ["manage-export-formats"],
  exportHistory: ["view-reports"],
  outputs: ["manage-export-formats"],
  // Connections and Connected Agents are the exception in this table: their
  // routes gate on `hasFormEditPermission` (form.edit OR admin, read off the
  // session) rather than a tenant capability, so the nav must name THAT
  // contract or a `manage-input-channels` role would be shown a door that
  // 403s. Unifying the two gate mechanisms is logged as F-065 follow-up.
  connections: ["form.edit", "admin"],
  agents: ["form.edit", "admin"],

  // --- System ---
  llm: ["tenant-settings"],
  llmUsage: ["view-analytics"],
  roles: ["manage-roles"],
  tenantSettings: ["tenant-settings"],
  reports: ["view-reports"],
  analytics: ["view-analytics"],
};

const ALL_KEYS = Object.keys(ADMIN_NAV_CAPABILITIES) as AdminNavKey[];

/** True when the session may see this Admin nav entry. */
export function canSeeAdminNavEntry(
  permissions: readonly string[] | undefined,
  key: AdminNavKey,
): boolean {
  if (!permissions?.length) return false;
  return ADMIN_NAV_CAPABILITIES[key].some((capability) => permissions.includes(capability));
}

/**
 * True when ANY Admin nav entry is visible — i.e. whether the Admin section and
 * its separator should render at all. Derived from the table above so it cannot
 * drift from the entries it introduces.
 */
export function canSeeAdminNav(permissions: readonly string[] | undefined): boolean {
  return ALL_KEYS.some((key) => canSeeAdminNavEntry(permissions, key));
}
